import { Button, Checkbox, InputNumber, message } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import { BodyText, H3 } from 'src/branding/typography'
import FoodSafetyCorrectiveActionPanel from 'src/components/food-safety/FoodSafetyCorrectiveActionPanel'
import {
  FoodSafetySubmitErrorBox,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_DELIVERY_TEMP_BAND,
  FOOD_SAFETY_RECENT_RECEIPTS_DAYS,
  FOOD_SAFETY_RECENT_RECEIPTS_LIMIT,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SECTION,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  createFoodSafetyDeliveryBatch,
  fetchFoodSafetyReceipt,
  fetchFoodSafetyRecentReceipts,
} from 'src/utils/foodSafetyApi'
import {
  FOOD_SAFETY_DELIVERY_ACCEPT_LABEL,
  FOOD_SAFETY_DELIVERY_SAMPLING_GUIDANCE,
  FOOD_SAFETY_DELIVERY_REJECT_LABEL,
  adHocRangeHint,
} from 'src/utils/foodSafetyAdHoc'
import {
  FOOD_SAFETY_RECEIPT_BAND_OPTIONS,
  FOOD_SAFETY_RECEIPT_LIST_HINT,
  FOOD_SAFETY_RECEIPT_OPEN_HINT,
  buildReceiptBatchBody,
  emptyReceiptCheck,
  formatReceiptBatchSaved,
  formatReceiptLastCheck,
  formatReceiptMeta,
  formatReceiptProductQuantity,
  formatReceiptTitle,
  isReceiptCheckOutOfRange,
  isReceiptProductCheckable,
  matchReceiptDeliveryTemplate,
  receiptCheckConfig,
  receiptKey,
  receiptProductUnavailableReason,
  validateReceiptChecks,
} from 'src/utils/foodSafetyReceiptChecks'
import {
  FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE,
  createFoodSafetySubmissionKey,
  refreshAfterFoodSafetySave,
} from 'src/utils/foodSafetySaveGuard'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'

const DELIVERIES = FOOD_SAFETY_SECTION.DELIVERIES
const VISIBLE_RECEIPTS = 4

/**
 * Deliveries: pick a recent OMS receipt, tick products, record temp/decision,
 * save together. Hidden when there are no recent receipts (or Express is too
 * old to list them) — the manual form below always stays. `initialReceipt`
 * (from Goods Receipting) opens that receipt straight away, even if it's older
 * than the recent list.
 */
const FoodSafetyRecentDeliveries = ({
  venueId,
  localDate,
  embedded = false,
  templates = [],
  initialReceipt = null,
  onSaved,
}) => {
  const [receipts, setReceipts] = useState([])
  const [showAll, setShowAll] = useState(false)
  const [older, setOlder] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [opening, setOpening] = useState(null)
  const [openError, setOpenError] = useState(null) // { row, message }
  const [receipt, setReceipt] = useState(null)
  const [checks, setChecks] = useState({})
  const [lineError, setLineError] = useState(null)
  const [submitError, setSubmitError] = useState(null)
  const [saving, setSaving] = useState(false)
  const openedInitialRef = useRef(null)
  const submissionKeyRef = useRef(null)
  const saveLock = useFoodSafetySaveLock()

  const loadReceipts = useCallback(async () => {
    if (!venueId) return
    if (older) setLoadingOlder(true)
    try {
      const data = await fetchFoodSafetyRecentReceipts(
        venueId,
        older
          ? {
              days: FOOD_SAFETY_RECENT_RECEIPTS_DAYS.max,
              limit: FOOD_SAFETY_RECENT_RECEIPTS_LIMIT.max,
            }
          : {},
      )
      setReceipts(data.receipts)
    } catch (error) {
      if (older) {
        // Keep the 7-day list; offer "Show older" again.
        message.error(error?.message || 'Couldn’t load older deliveries')
        setOlder(false)
      } else {
        setReceipts([])
      }
    } finally {
      setLoadingOlder(false)
    }
  }, [venueId, older])

  useEffect(() => {
    loadReceipts()
  }, [loadReceipts])

  const showOlder = () => {
    setShowAll(true)
    setOlder(true)
  }

  const openReceipt = async row => {
    const key = receiptKey(row)
    setOpening(key)
    setOpenError(null)
    setSubmitError(null)
    setLineError(null)
    try {
      const detail = await fetchFoodSafetyReceipt(
        venueId,
        row.purchaseOrderNumber,
        row.supplier?._id ? { supplierId: row.supplier._id } : {},
      )
      const products = Array.isArray(detail?.products) ? detail.products : []
      const next = {}
      products.forEach(product => {
        next[product.orderLineId] = emptyReceiptCheck(
          matchReceiptDeliveryTemplate(templates, product.productName),
          product.productName,
        )
      })
      setChecks(next)
      submissionKeyRef.current = createFoodSafetySubmissionKey()
      setReceipt({ ...detail, products })
    } catch (error) {
      setOpenError({
        row,
        message: error?.message || 'Check your connection and try again.',
      })
    } finally {
      setOpening(null)
    }
  }

  const initialKey = initialReceipt?.purchaseOrderNumber
    ? `${initialReceipt.purchaseOrderNumber}|${initialReceipt.supplierId || ''}`
    : null

  useEffect(() => {
    if (!venueId || !initialKey || openedInitialRef.current === initialKey) {
      return
    }
    openedInitialRef.current = initialKey
    openReceipt({
      purchaseOrderNumber: initialReceipt.purchaseOrderNumber,
      supplier: initialReceipt.supplierId
        ? { _id: initialReceipt.supplierId }
        : null,
    })
  }, [venueId, initialKey])

  const closeReceipt = () => {
    submissionKeyRef.current = null
    setReceipt(null)
    setChecks({})
    setLineError(null)
    setSubmitError(null)
  }

  const patchCheck = (orderLineId, patch) => {
    setLineError(null)
    setSubmitError(null)
    setChecks(prev => ({
      ...prev,
      [orderLineId]: { ...prev[orderLineId], ...patch },
    }))
  }

  const patchAction = (orderLineId, patch) => {
    setLineError(null)
    setSubmitError(null)
    setChecks(prev => ({
      ...prev,
      [orderLineId]: {
        ...prev[orderLineId],
        action: { ...prev[orderLineId]?.action, ...patch },
      },
    }))
  }

  const products = receipt?.products || []
  const checkable = products.filter(isReceiptProductCheckable)
  const tickedCount = checkable.filter(
    p => checks[p.orderLineId]?.checked,
  ).length
  const allTicked = checkable.length > 0 && tickedCount === checkable.length

  const toggleAll = () => {
    setLineError(null)
    setChecks(prev => {
      const next = { ...prev }
      checkable.forEach(p => {
        next[p.orderLineId] = { ...next[p.orderLineId], checked: !allTicked }
      })
      return next
    })
  }

  const submit = async () => {
    if (saveLock.isHeld()) return
    const problem = validateReceiptChecks({ products, checks, templates })
    if (problem) {
      setLineError(problem)
      return
    }
    if (!saveLock.tryAcquire()) return
    if (!submissionKeyRef.current) {
      submissionKeyRef.current = createFoodSafetySubmissionKey()
    }
    setSaving(true)
    setSubmitError(null)
    try {
      let result
      try {
        result = await createFoodSafetyDeliveryBatch(venueId, {
          ...buildReceiptBatchBody({
            receipt,
            localDate,
            products,
            checks,
            templates,
          }),
          submissionKey: submissionKeyRef.current,
        })
      } catch (error) {
        setSubmitError(error?.message || 'Couldn’t save the delivery checks')
        return
      }
      message.success(formatReceiptBatchSaved(result))
      closeReceipt()
      loadReceipts()
      if (!(await refreshAfterFoodSafetySave(() => onSaved?.(result)))) {
        message.warning(FOOD_SAFETY_SAVED_REFRESH_FAILED_MESSAGE)
      }
    } finally {
      saveLock.release()
      setSaving(false)
    }
  }

  if (!receipt && receipts.length === 0 && !openError) return null

  if (!receipt) {
    const visible = showAll ? receipts : receipts.slice(0, VISIBLE_RECEIPTS)
    const hidden = receipts.length - visible.length
    return (
      <Card $embedded={embedded} data-testid='recent-deliveries'>
        <Title>Recent OMS deliveries</Title>
        {openError ? (
          <FoodSafetySubmitErrorBox
            role='alert'
            data-testid='recent-delivery-open-error'
          >
            <div>
              Couldn’t open delivery
              {openError.row?.purchaseOrderNumber
                ? ` ${openError.row.purchaseOrderNumber}`
                : ''}
              . {openError.message}
            </div>
            <Button
              size='small'
              loading={Boolean(opening)}
              onClick={() => openReceipt(openError.row)}
              data-testid='recent-delivery-open-retry'
            >
              Try again
            </Button>
          </FoodSafetySubmitErrorBox>
        ) : null}
        {receipts.length > 0 ? (
          <Hint data-testid='recent-deliveries-hint'>
            {FOOD_SAFETY_RECEIPT_LIST_HINT}
          </Hint>
        ) : null}
        <ReceiptList>
          {visible.map(row => {
            const key = receiptKey(row)
            return (
              <ReceiptRow
                key={key}
                type='button'
                onClick={() => openReceipt(row)}
                disabled={Boolean(opening)}
                data-testid={`recent-delivery-${row.purchaseOrderNumber}`}
              >
                <ReceiptTitle>{formatReceiptTitle(row)}</ReceiptTitle>
                <ReceiptMeta>
                  {opening === key ? 'Opening…' : formatReceiptMeta(row)}
                </ReceiptMeta>
              </ReceiptRow>
            )
          })}
        </ReceiptList>
        <ListLinks>
          {hidden > 0 || showAll ? (
            <LinkButton
              type='button'
              onClick={() => setShowAll(v => !v)}
              data-testid='recent-deliveries-toggle'
            >
              {showAll ? 'Show fewer' : `Show ${hidden} more`}
            </LinkButton>
          ) : null}
          {!older ? (
            <LinkButton
              type='button'
              onClick={showOlder}
              data-testid='recent-deliveries-older'
            >
              {`Show older receipts (last ${FOOD_SAFETY_RECENT_RECEIPTS_DAYS.max} days)`}
            </LinkButton>
          ) : loadingOlder ? (
            <Hint data-testid='recent-deliveries-older-loading'>
              Loading older receipts…
            </Hint>
          ) : (
            <Hint data-testid='recent-deliveries-older-note'>
              {`Receipts from the last ${FOOD_SAFETY_RECENT_RECEIPTS_DAYS.max} days`}
            </Hint>
          )}
        </ListLinks>
      </Card>
    )
  }

  return (
    <Card $embedded={embedded} data-testid='recent-delivery-open'>
      <HeaderRow>
        <div>
          <Title>{formatReceiptTitle(receipt)}</Title>
          <Hint>Supplier: {receipt.supplier?.name || 'Not recorded'}</Hint>
          <Hint>{FOOD_SAFETY_DELIVERY_SAMPLING_GUIDANCE}</Hint>
          <Hint data-testid='recent-delivery-meta'>
            {formatReceiptMeta({
              ...receipt,
              receiptedLineCount: checkable.length,
            })}
          </Hint>
          <Hint data-testid='recent-delivery-optional'>
            {FOOD_SAFETY_RECEIPT_OPEN_HINT}
          </Hint>
        </div>
        <LinkButton
          type='button'
          onClick={closeReceipt}
          data-testid='recent-delivery-back'
        >
          Back
        </LinkButton>
      </HeaderRow>

      {checkable.length > 1 ? (
        <LinkButton
          type='button'
          onClick={toggleAll}
          data-testid='recent-delivery-toggle-all'
        >
          {allTicked ? 'Untick all' : 'Select all products to check'}
        </LinkButton>
      ) : null}

      <ProductList>
        {products.map(product => {
          const id = product.orderLineId
          const check = checks[id] || emptyReceiptCheck()
          const unavailable = receiptProductUnavailableReason(product)
          const template = matchReceiptDeliveryTemplate(
            templates,
            product.productName,
          )
          const config = receiptCheckConfig(check, template)
          const rejected = check.accepted === false
          const outOfRange = isReceiptCheckOutOfRange(check, template)
          const needsPanel = check.checked && (rejected || outOfRange)
          const lastCheck = formatReceiptLastCheck(product.lastCheck)
          const error = lineError?.orderLineId === id ? lineError.message : null
          return (
            <ProductRow
              key={id}
              data-checked={check.checked ? 'true' : 'false'}
              data-testid={`recent-delivery-product-${id}`}
            >
              <ProductHead>
                <span data-testid={`recent-delivery-tick-${id}`}>
                  <Checkbox
                    checked={Boolean(check.checked)}
                    disabled={Boolean(unavailable)}
                    onChange={e =>
                      patchCheck(id, { checked: e.target.checked })
                    }
                  >
                    {product.productName}
                  </Checkbox>
                </span>
                <ProductMeta data-testid={`recent-delivery-meta-${id}`}>
                  {unavailable ||
                    [formatReceiptProductQuantity(product), lastCheck]
                      .filter(Boolean)
                      .join(' · ')}
                </ProductMeta>
              </ProductHead>

              {check.checked ? (
                <ProductBody>
                  {template ? (
                    <Hint>{adHocRangeHint(DELIVERIES, config)}</Hint>
                  ) : (
                    <BandRow>
                      {FOOD_SAFETY_RECEIPT_BAND_OPTIONS.map(opt => (
                        <BandChip
                          key={opt.band}
                          type='button'
                          data-active={
                            check.band === opt.band ? 'true' : 'false'
                          }
                          aria-pressed={check.band === opt.band}
                          onClick={() =>
                            patchCheck(id, {
                              band: opt.band,
                              bandGuessed: false,
                            })
                          }
                          data-testid={`recent-delivery-band-${id}-${opt.band}`}
                        >
                          {opt.label}
                        </BandChip>
                      ))}
                      {check.bandGuessed ? (
                        <Hint data-testid={`recent-delivery-band-guess-${id}`}>
                          Guessed from the name — change it if wrong.
                        </Hint>
                      ) : null}
                    </BandRow>
                  )}
                  <EntryRow>
                    <InputNumber
                      value={check.value}
                      onChange={value => patchCheck(id, { value })}
                      placeholder={
                        config.tempBand ===
                        FOOD_SAFETY_DELIVERY_TEMP_BAND.AMBIENT
                          ? 'Temp (optional)'
                          : 'Temp °C'
                      }
                      step={0.1}
                      aria-label={`${product.productName} temperature (°C)`}
                      data-testid={`recent-delivery-temp-${id}`}
                    />
                    <DecisionChip
                      type='button'
                      data-active={!rejected ? 'true' : 'false'}
                      data-tone='accept'
                      aria-pressed={!rejected}
                      onClick={() => patchCheck(id, { accepted: true })}
                      data-testid={`recent-delivery-accept-${id}`}
                    >
                      {FOOD_SAFETY_DELIVERY_ACCEPT_LABEL}
                    </DecisionChip>
                    <DecisionChip
                      type='button'
                      data-active={rejected ? 'true' : 'false'}
                      data-tone='reject'
                      aria-pressed={rejected}
                      onClick={() => patchCheck(id, { accepted: false })}
                      data-testid={`recent-delivery-reject-${id}`}
                    >
                      {FOOD_SAFETY_DELIVERY_REJECT_LABEL}
                    </DecisionChip>
                  </EntryRow>
                  {needsPanel ? (
                    <FoodSafetyCorrectiveActionPanel
                      section={DELIVERIES}
                      variant={rejected ? 'rejected' : 'action'}
                      reading={{
                        value: check.value,
                        tempMin: config.tempMin,
                        tempMax: config.tempMax,
                      }}
                      draft={check.action}
                      onChange={patch => patchAction(id, patch)}
                      error={error}
                      testIdPrefix={`recent-delivery-corrective-${id}`}
                    />
                  ) : null}
                  {error && !needsPanel ? (
                    <LineError
                      role='alert'
                      data-testid={`recent-delivery-error-${id}`}
                    >
                      {error}
                    </LineError>
                  ) : null}
                </ProductBody>
              ) : null}
            </ProductRow>
          )
        })}
      </ProductList>

      {lineError && !lineError.orderLineId ? (
        <LineError role='alert' data-testid='recent-delivery-error'>
          {lineError.message}
        </LineError>
      ) : null}
      {submitError ? (
        <FoodSafetySubmitErrorBox
          role='status'
          data-testid='recent-delivery-submit-error'
        >
          {submitError}
        </FoodSafetySubmitErrorBox>
      ) : null}

      <Button
        type='primary'
        loading={saving}
        disabled={tickedCount === 0}
        onClick={submit}
        data-testid='recent-delivery-submit'
      >
        {tickedCount
          ? `Save ${tickedCount} check${tickedCount === 1 ? '' : 's'}`
          : 'Tick products to check'}
      </Button>
    </Card>
  )
}

export default FoodSafetyRecentDeliveries

const Card = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ $embedded, theme }) =>
    $embedded ? '0' : theme.spacing.md};
  flex: ${({ $embedded }) => ($embedded ? '1 1 16rem' : 'none')};
`

const Title = styled(H3)`
  margin: 0;
  font-size: 1rem;
`

const Hint = styled(BodyText)`
  display: block;
  margin: 0;
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;

  > div {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  > button {
    flex-shrink: 0;
    white-space: nowrap;
  }
`

const ReceiptList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const ReceiptRow = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.1rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  }
`

const ReceiptTitle = styled.span`
  font-weight: 600;
  overflow-wrap: anywhere;
`

const ReceiptMeta = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ListLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 1rem;
`

const LinkButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const ProductRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};

  &[data-checked='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  }
`

const ProductHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.25rem 0.75rem;
`

const ProductMeta = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ProductBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const BandRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const BandChip = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  padding: 0.2rem 0.65rem;
  cursor: pointer;
  font-size: 0.8rem;

  &[data-active='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  }
`

const EntryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`

const DecisionChip = styled.button`
  min-height: 2.25rem;
  padding: 0.35rem 0.75rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1.5px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  background: ${({ theme }) => theme.colours.mayoWhite || '#fff'};
  font-size: 0.9rem;
  cursor: pointer;

  &[data-active='true'][data-tone='accept'] {
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    font-weight: 700;
  }

  &[data-active='true'][data-tone='reject'] {
    background: #fff2e8;
    border-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    font-weight: 700;
  }
`

const LineError = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #c45c26;
`
